import { ElementNode, $applyNodeReplacement } from "lexical";
import {
    close_graph,
    download_Graph,
    content_copy_Graph,
} from "../../../../../../assets/images/images";
import {
    _floatingTooltip,
    createIconButton,
    copyImageToClipboard,
    downloadImage,
    getCopyableImageAttrs,
} from "../../../../../../utils/utlis";

// Editor-only controls (copy / download). Stripped in exportDOM so they never
// reach saved lesson HTML.
const IMAGE_ACTIONS_CLASS = "graph_image_actions";

// Full-screen preview, mirroring the popup ProcessRenderLatex opens for images.
// Plain DOM because Lexical nodes render outside React.
const openImagePreview = (src) => {
    if (!src) return;

    const overlay = document.createElement("div");
    overlay.style.cssText = `
        position: fixed;
        inset: 0;
        z-index: 1400;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    `;

    const img = document.createElement("img");
    img.src = src;
    img.alt = "Full Preview";
    img.style.cssText =
        "width:70%;height:70%;object-fit:contain;border-radius:8px;";
    overlay.appendChild(img);

    const close = () => {
        overlay.remove();
        document.removeEventListener("keydown", onKeyDown);
    };
    const onKeyDown = (e) => {
        if (e.key === "Escape") close();
    };

    overlay.addEventListener("click", close);
    document.addEventListener("keydown", onKeyDown);
    document.body.appendChild(overlay);
};


export class ImageNode extends ElementNode {
    __src;
    __alt;
    __className; // ✅ NEW: holds the original class attribute (e.g. "picture_submission", "graph-image")

    static getType() {
        return "image";
    }

    static clone(node) {
        return new ImageNode(node.__src, node.__alt, node.__className, node.__key); // ✅ pass className through
    }

    constructor(src, alt, className, key) {
        super(key);
        this.__src = src;
        this.__alt = alt;
        this.__className = className; // ✅ store it
    }


    createDOM() {
        if (this.__src.includes("graph_close_image")) {
            const img = document.createElement("img");
            img.src = this.__src;
            img.alt = this.__alt;
            img.style.maxWidth = "100%";
            return img;
        }

        const wrapper = document.createElement("div");
        wrapper.className = "graph_image";
        wrapper.style.position = "relative";
        wrapper.style.display = "inline-block";
        wrapper.style.width = "90%";
        wrapper.style.textAlign = "center";
        wrapper.style.marginTop = "25px";


        const img = document.createElement("img");
        img.src = this.__src;
        img.alt = this.__alt;
        img.style.maxWidth = "100%";
        img.style.borderRadius = "8px";

        // ✅ re-apply the original class (picture_submission, graph-image, etc.)
        if (this.__className) {
            img.className = this.__className;
        }

        // click-to-preview, same as rendered lesson images
        img.style.cursor = "zoom-in";
        img.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            openImagePreview(img.currentSrc || img.src);
        });

        // Copy / download, placed to the left of the remove (X) icon
        const actions = document.createElement("div");
        actions.className = IMAGE_ACTIONS_CLASS;
        actions.contentEditable = "false";
        actions.style.cssText =
            "position:absolute;top:-25px;right:36px;display:flex;gap:6px;";
        // keep the editor caret/selection where it is when a button is pressed
        actions.addEventListener("mousedown", (e) => e.preventDefault());

        const copyBtn = createIconButton({
            iconSrc: content_copy_Graph,
            title: "Copy image",
            alt: "copy",
            size: 20,
            onClick: () => {
                copyImageToClipboard(
                    img.currentSrc || img.src,
                    img.alt || "",
                    getCopyableImageAttrs(img),
                );
            },
        });

        const downloadBtn = createIconButton({
            iconSrc: download_Graph,
            title: "Download image",
            size: 22,
            onClick: () => {
                downloadImage(img.currentSrc || img.src, img.alt || "");
            },
        });

        actions.append(copyBtn, downloadBtn);
        // icon <img>s must not be re-imported as ImageNodes on paste
        actions.querySelectorAll("img").forEach((icon) => {
            icon.dataset.internal = "true";
        });

        const span = document.createElement("span");
        span.className = "graph_close_image";
        span.dataset.nodeKey = this.getKey();
        span.style.position = "absolute";
        span.style.top = "-20px";
        span.style.right = "10px";
        span.style.cursor = "pointer";


        const icon = document.createElement("img");
        icon.src = close_graph;
        icon.dataset.internal = "true";
        icon.style.width = "16px";
        icon.style.height = "16px";

        span.appendChild(icon);
        wrapper.appendChild(img);
        wrapper.appendChild(actions);
        wrapper.appendChild(span);

        span.addEventListener("mouseenter", (e) => {
            const rect = span.getBoundingClientRect();
            _floatingTooltip.show("Remove image", rect);
        });
        span.addEventListener("mouseleave", () => {
            _floatingTooltip.hide();
        });
        span.addEventListener("click", () => {
            _floatingTooltip.hide(0);
        });

        return wrapper;
    }


    updateDOM(prevNode, dom) {
        // dom here is the wrapper <div> (or bare <img> for the close-icon case),
        // so we need to find the actual <img> to update — `dom.src = ...` on a div is a no-op.
        const targetImg = dom.tagName === "IMG" ? dom : dom.querySelector("img");
        if (!targetImg) return false;

        if (prevNode.__src !== this.__src) {
            targetImg.src = this.__src;
        }
        if (prevNode.__alt !== this.__alt) {
            targetImg.alt = this.__alt;
        }
        if (prevNode.__className !== this.__className && this.__className) { // ✅ keep class in sync too
            targetImg.className = this.__className;
        }
        return false;
    }

    // Default ElementNode export serialises createDOM(); drop the editor-only
    // copy/download controls so saved HTML is unchanged from before.
    exportDOM(editor) {
        const { element, ...rest } = super.exportDOM(editor);
        element
            ?.querySelectorAll?.(`.${IMAGE_ACTIONS_CLASS}`)
            .forEach((node) => node.remove());
        return { element, ...rest };
    }

    static importDOM() {
        return {
            img: (node) => {

                if (node.dataset.internal === "true") return null;


                const src = node.getAttribute("src");
                const alt = node.getAttribute("alt") || "";
                const className = node.getAttribute("class") || undefined; // ✅ read the class attribute

                if (
                    !src ||
                    src.includes("close-graph") ||
                    src.includes("graph_close_image") ||
                    node.closest(".graph_close_image")
                ) {
                    return null;
                }

                return {
                    conversion: () => ({ node: new ImageNode(src, alt, className) }), // ✅ pass it into the node
                    priority: 2,
                };
            },

            div: (domNode) => {
                const className = domNode.getAttribute("class");
                if (className && className.includes("graph_image")) {
                    return null;
                }
                return undefined;
            },
        };
    }



    static importJSON(serializedNode) {
        return new ImageNode(serializedNode.src, serializedNode.alt, serializedNode.className); // ✅ restore on load
    }

    exportJSON() {
        return {
            type: "image",
            src: this.__src,
            alt: this.__alt,
            className: this.__className, // ✅ persist in saved JSON
            version: 1,
            children: [],
        };
    }

}

export function $createImageNode(src, alt = "", className = "") {
    // Backward compatible — existing calls like $createImageNode(src, alt) still work fine.
    return $applyNodeReplacement(new ImageNode(src, alt, className));
}